import { Controller } from "@hotwired/stimulus";
import { DICE, diceFor, type RollSpec } from "../utils/spell_roll";

type Field = HTMLInputElement | HTMLSelectElement;
type Cell = { label: string; value: string; invalid?: boolean };

const LEVELS = [1, 3, 5, 7, 10, 15];
const CIRCLES = [1, 2, 3, 4, 5, 6, 7];
const COUNT_KEYS = ["base_dice", "dice", "base_flat", "flat"] as const;
const SCALING_KEYS = ["every", "base_dice", "dice", "die", "base_flat", "flat", "cap_dice", "cap_flat"] as const;

// The spell form's Rolagem section: shows the fields of the chosen kind, keeps the
// other kinds' fields disabled so they are not submitted, and writes the "Na ficha"
// strip with the same math as Spell::Roll. Every sentence comes from the server.
export default class extends Controller<HTMLElement> {
	static targets = ["kind", "mechanic", "group", "label", "labelField", "sheetNone", "problem", "cells", "cell", "unitMode"];
	static values = { phrases: Object };

	declare readonly kindTarget: HTMLSelectElement;
	declare readonly mechanicTarget: HTMLInputElement;
	declare readonly groupTargets: HTMLElement[];
	declare readonly labelTarget: HTMLInputElement;
	declare readonly labelFieldTarget: HTMLElement;
	declare readonly sheetNoneTarget: HTMLElement;
	declare readonly problemTarget: HTMLElement;
	declare readonly cellsTarget: HTMLElement;
	declare readonly cellTarget: HTMLTemplateElement;
	declare readonly unitModeTarget: HTMLSelectElement;
	declare readonly phrasesValue: Record<string, string>;

	connect() {
		this.update();
	}

	update() {
		const kind = this.kindTarget.value;
		this.mechanicTarget.value = this.kindTarget.selectedOptions[0]?.dataset.mechanic ?? "none";
		this.labelFieldTarget.hidden = kind === "none";
		this.labelTarget.disabled = kind === "none";
		for (const group of this.groupTargets) {
			const active = group.dataset.kind === kind;
			group.hidden = !active;
			for (const field of group.querySelectorAll<Field>("input, select")) field.disabled = !active;
		}
		for (const part of this.element.querySelectorAll<HTMLElement>("[data-unit-mode]")) {
			const on = part.dataset.unitMode === this.unitModeTarget.value;
			part.hidden = !on;
			if (!on) for (const field of part.querySelectorAll<Field>("input, select")) field.disabled = true;
		}

		const cells = this.cells(kind);
		const problem = this.problem(kind) || (cells.find((cell) => cell.invalid)?.value ?? "");
		this.problemTarget.hidden = problem === "";
		if (this.problemTarget.textContent !== problem) this.problemTarget.textContent = problem;
		this.sheetNoneTarget.hidden = kind !== "none";
		this.cellsTarget.replaceChildren(...cells.map((cell) => this.render(cell)));
	}

	private say(key: string, vars: Record<string, string | number> = {}) {
		return (this.phrasesValue[key] ?? "").replace(/%\{(\w+)\}/g, (_, name) => String(vars[name] ?? ""));
	}

	private render({ label, value, invalid }: Cell) {
		const node = this.cellTarget.content.cloneNode(true) as DocumentFragment;
		const text = node.querySelector<HTMLElement>('[data-part="value"]');
		const caption = node.querySelector<HTMLElement>('[data-part="label"]');
		if (caption) caption.textContent = label;
		if (text) {
			text.textContent = value;
			text.dataset.invalid = String(Boolean(invalid));
		}
		return node;
	}

	private group(kind: string) {
		return this.groupTargets.find((group) => group.dataset.kind === kind);
	}

	private text(kind: string, key: string) {
		return (this.group(kind)?.querySelector<Field>(`[data-key="${key}"]`)?.value ?? "").trim();
	}

	private number(kind: string, key: string) {
		const value = this.text(kind, key);
		return value === "" || Number.isNaN(Number(value)) ? undefined : Number(value);
	}

	private spec(kind: string, prefix: string): RollSpec {
		const spec: Record<string, number> = {};
		for (const key of SCALING_KEYS) {
			const value = this.number(kind, `${prefix}${key}`);
			if (value !== undefined) spec[key] = value;
		}
		return spec;
	}

	private unitMode() {
		return this.unitModeTarget.value;
	}

	// What each one rolls at a level, or undefined when the unit is not a valid roll.
	private unitFor(kind: string): (level: number) => string | undefined {
		if (this.unitMode() === "fixed") {
			const dice = this.text(kind, "unit.dice");
			return () => (DICE.test(dice) ? dice : undefined);
		}
		const unit = this.spec(kind, "unit.scaling.");
		const rolled = (unit.base_dice ?? 0) + (unit.dice ?? 0) > 0;
		return (n) => (rolled ? diceFor(unit, n) : undefined);
	}

	// The two errors the server rejects without a cell of their own.
	private problem(kind: string) {
		if (kind === "circle" && CIRCLES.every((n) => this.text(kind, `by_circle.${n}`) === "")) return this.say("circle_empty");
		const counted = COUNT_KEYS.some((key) => this.text(kind, `count.scaling.${key}`) !== "");
		return kind === "count" && !counted ? this.say("count_empty") : "";
	}

	private cells(kind: string): Cell[] {
		const label = this.labelTarget.value.trim() || this.say("default_label");
		const level = (n: number) => this.say("level", { level: n });

		switch (kind) {
			case "scaling": {
				const spec = this.spec(kind, "scaling.");
				return LEVELS.map((n) => ({ label: level(n), value: `${label} ${diceFor(spec, n)}` }));
			}
			case "fixed": {
				const dice = this.text(kind, "dice");
				return DICE.test(dice)
					? [{ label: this.say("any_level"), value: `${label} ${dice}` }]
					: [{ label: this.say("any_level"), value: this.say("fixed_error"), invalid: true }];
			}
			case "circle":
				return CIRCLES.map((n) => {
					const dice = this.text(kind, `by_circle.${n}`);
					const circle = this.say("circle", { circle: n });
					if (dice === "") return { label: circle, value: this.say("no_roll") };
					return DICE.test(dice) ? { label: circle, value: `${label} ${dice}` } : { label: circle, value: this.say("circle_error"), invalid: true };
				});
			case "count": {
				const count = this.spec(kind, "count.scaling.");
				const countLabel = this.text(kind, "count_label");
				const unit = this.unitFor(kind);
				return LEVELS.map((n) => {
					const each = unit(n);
					if (each === undefined) return { label: level(n), value: this.say(this.unitMode() === "fixed" ? "unit_error" : "unit_empty"), invalid: true };
					const total = diceFor(count, n);
					if (!total.includes("d")) return { label: level(n), value: `${label} ${total}×${each}` };
					const name = countLabel ? countLabel.charAt(0).toUpperCase() + countLabel.slice(1) : this.say("count_default");
					return { label: level(n), value: `${name} ${total} · ${label} ${each}` };
				});
			}
			default:
				return [];
		}
	}
}
